import React, { useCallback, useMemo, useState } from "react";

import type { SalaryPreference } from ".";
import { Button, CheckChip, ErrorText, Label, Flex } from "../../../../../../../../../../mads";
import { ChevronDown, ChevronUp } from "../../../../../../../../../../mads/icons";
import { useI18n } from "../../../../../../../../../../shared/I18n";
import { getPayTypesToLabels } from "../../../../../../../../../../shared/util/salary/getPayTypesToLabels";
import { SalaryType } from "../../../../../../../../../../shared/util/salary/SalaryType";
import { styles } from "./styles";

const INITIAL_SHOW_COUNT = 2;

export const PayPeriodSelect = ({
  invalid,
  value,
  onChange,
  errorText,
  showRequiredIndicator = false,
}: {
  invalid?: boolean;
  value?: SalaryPreference;
  onChange: (salaryPreference: SalaryPreference) => void;
  errorText?: string;
  showRequiredIndicator?: boolean;
}) => {
  const PAY_OPTIONS_ORDER = useMemo(
    () => [
      SalaryType.Hourly,
      SalaryType.Yearly,
      SalaryType.Monthly,
      SalaryType.Weekly,
      SalaryType.Daily,
    ],
    [],
  );
  const gt = useI18n();
  const amount = value?.amount ?? "";
  const salaryType = value?.salaryType ?? "";

  const payPeriodLabel = gt.pgettext("Pay period form field label", "Pay period");

  const payTypesToLabels = getPayTypesToLabels(gt);

  // The pay type is pre-selected based on the job's pay type.
  // If the selected pay type is below the fold, show all on load.
  const shouldBeExpandedOnInitialLoad =
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison -- FIXME
    PAY_OPTIONS_ORDER.slice(INITIAL_SHOW_COUNT).filter((payType) => payType === salaryType).length >
    0;

  const [isExpanded, setIsExpanded] = useState(shouldBeExpandedOnInitialLoad);

  const selectSalaryType = useCallback(
    (selected: string) => onChange({ amount, salaryType: selected }),
    [amount, onChange],
  );

  const items = useMemo(
    () =>
      PAY_OPTIONS_ORDER.slice(0, isExpanded ? undefined : INITIAL_SHOW_COUNT).map((payType) => ({
        value: payType,
        label: `${payTypesToLabels[payType][0].toUpperCase()}${payTypesToLabels[payType].slice(1)}`,
      })),
    [isExpanded, payTypesToLabels, PAY_OPTIONS_ORDER],
  );

  return (
    <Flex sx={styles.inputBox}>
      <Label text={payPeriodLabel} isRequired={showRequiredIndicator} />
      <Flex sx={{ flexDirection: "row", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
        {items.map((item) => (
          <CheckChip
            key={item.value}
            label={item.label}
            size={"lg"}
            multiSelect={false}
            checked={salaryType === item.value.toString()}
            onPress={() => selectSalaryType(item.value)}
          />
        ))}
      </Flex>
      {errorText && invalid && <ErrorText>{errorText}</ErrorText>}
      <Button
        variant="text"
        onPress={() => setIsExpanded(!isExpanded)}
        iconAfter={isExpanded ? <ChevronUp aria-hidden /> : <ChevronDown aria-hidden />}
        sx={{ paddingLeft: 0, paddingRight: 0 }}
      >
        {isExpanded ? gt.gettext("Show less pay periods") : gt.gettext("Show more pay periods")}
      </Button>
    </Flex>
  );
};
